import { Button } from "@/components/ui/button";
import { ExternalLink, Github, Dribbble, Linkedin, Mail, ArrowUpRight } from "lucide-react";
import EditableText from "./EditableText";
import type { WebsiteCustomizations } from "./PremiumTemplate";

interface Props {
  name: string; desc: string; product: string; city: string;
  customizations?: WebsiteCustomizations;
  editMode?: boolean;
  onCustomize?: (field: keyof WebsiteCustomizations, value: string) => void;
}

const projects = [
  { title: "Refonte identité visuelle", cat: "Branding", color: "from-violet-500/30 to-purple-800/40" },
  { title: "Application mobile", cat: "UI/UX", color: "from-pink-500/30 to-rose-800/40" },
  { title: "Site e-commerce", cat: "Web Design", color: "from-blue-500/30 to-indigo-800/40" },
  { title: "Campagne publicitaire", cat: "Direction artistique", color: "from-amber-500/30 to-orange-800/40" },
  { title: "Dashboard SaaS", cat: "Product Design", color: "from-emerald-500/30 to-teal-800/40" },
  { title: "Packaging produit", cat: "Branding", color: "from-red-500/30 to-rose-800/40" },
];

const PortfolioTemplate = ({ name, desc, product, city, customizations, editMode = false, onCustomize }: Props) => {
  const c = customizations || {};
  const edit = (field: keyof WebsiteCustomizations) => (v: string) => onCustomize?.(field, v);
  const displayName = c.heroTitle || name;
  const displayDesc = c.heroSubtitle || desc;
  const displayProduct = c.productName || product || "Design";
  const displayCta = c.ctaText || "Voir les projets";
  return (
  <div className="bg-[#0a0a0a] text-white min-h-screen">
    {/* Nav */}
    <nav className="fixed top-0 w-full z-50 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <EditableText as="span" value={displayName} onChange={edit("heroTitle")} editMode={editMode} className="text-lg font-bold text-violet-400" data-field="brand-name" />
        <div className="hidden md:flex items-center gap-8 text-sm text-gray-400">
          <span className="hover:text-white cursor-pointer transition-colors">Projets</span>
          <span className="hover:text-white cursor-pointer transition-colors">Services</span>
          <span className="hover:text-white cursor-pointer transition-colors">À propos</span>
          <Button className="bg-violet-600 hover:bg-violet-700 text-white h-9 px-5 rounded-full text-sm">Contact</Button>
        </div>
      </div>
    </nav>

    {/* Hero */}
    <section className="min-h-screen flex items-center pt-16 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-pink-600/10 rounded-full blur-[80px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-3xl space-y-8">
          <div className="flex items-center gap-3">
            <span className="h-3 w-3 rounded-full bg-violet-500 animate-pulse" />
            <span className="text-sm text-gray-400 font-medium">Disponible pour de nouveaux projets</span>
          </div>
          <h1 className="text-6xl md:text-8xl font-bold leading-[0.95] tracking-tight">
            <EditableText as="span" value={displayProduct} onChange={edit("productName")} editMode={editMode} className="text-gray-400" data-field="hero-title" /><br />
            <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">& créativité</span>
          </h1>
          <EditableText as="p" value={displayDesc} onChange={edit("heroSubtitle")} editMode={editMode} multiline className="text-xl text-gray-400 max-w-lg leading-relaxed" data-field="hero-subtitle" />
          <div className="flex items-center gap-6 pt-4">
            <Button className="bg-violet-600 hover:bg-violet-700 h-14 px-8 rounded-full text-base">
              <EditableText as="span" value={displayCta} onChange={edit("ctaText")} editMode={editMode} data-field="cta-primary" /> <ArrowUpRight className="ml-2 h-5 w-5" />
            </Button>
            <div className="flex items-center gap-4">
              {[Dribbble, Github, Linkedin].map((Icon, i) => (
                <a key={i} className="h-10 w-10 rounded-full border border-white/10 flex items-center justify-center hover:border-violet-500/50 hover:bg-violet-500/5 transition-all cursor-pointer">
                  <Icon className="h-4 w-4 text-gray-400" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Projects grid */}
    <section className="py-24 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-violet-400">Portfolio</span>
          <h2 className="text-4xl md:text-5xl font-bold mt-3">Projets sélectionnés</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((p, i) => (
            <div key={i} className="group cursor-pointer">
              <div className={`aspect-[16/10] rounded-2xl bg-gradient-to-br ${p.color} border border-white/5 relative overflow-hidden mb-4`}>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="h-14 w-14 rounded-full bg-violet-600 flex items-center justify-center">
                    <ExternalLink className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold group-hover:text-violet-400 transition-colors">{p.title}</h3>
                <span className="text-xs text-gray-500 font-medium px-2 py-1 rounded-full border border-white/10">{p.cat}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* About */}
    <section className="py-24 bg-white/[0.02] border-y border-white/5">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <div className="aspect-square rounded-3xl bg-gradient-to-br from-violet-600/20 to-pink-600/10 border border-white/5" />
        <div className="space-y-6">
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-violet-400">À propos</span>
          <h2 className="text-4xl font-bold">Créateur d'expériences digitales depuis {city}</h2>
          <p className="text-gray-400 leading-relaxed text-lg">{desc}. Spécialisé en design d'interface, identité visuelle et direction artistique pour des marques ambitieuses.</p>
          <div className="grid grid-cols-3 gap-6 pt-4">
            {[{ v: "8+", l: "Années" }, { v: "120+", l: "Projets" }, { v: "40+", l: "Clients" }].map((s) => (
              <div key={s.l}><p className="text-3xl font-bold text-violet-400">{s.v}</p><p className="text-sm text-gray-500 mt-1">{s.l}</p></div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-24">
      <div className="max-w-3xl mx-auto px-6 text-center space-y-8">
        <h2 className="text-4xl md:text-6xl font-bold">Un projet en tête ?</h2>
        <p className="text-xl text-gray-400">Discutons de votre vision et donnons-lui vie.</p>
        <Button className="bg-violet-600 hover:bg-violet-700 h-14 px-10 rounded-full text-base">
          <Mail className="mr-2 h-5 w-5" /> Me contacter
        </Button>
      </div>
    </section>

    {/* Footer */}
    <footer className="border-t border-white/5 py-8">
      <div className="max-w-7xl mx-auto px-6 text-center text-sm text-gray-500">
        © 2026 {name} — {city} — Propulsé par Grafton IA
      </div>
    </footer>
  </div>
  );
};

export default PortfolioTemplate;
